/** Color maths for the palette + contrast engine. Everything here is pure. */

export interface RGB {
  r: number;
  g: number;
  b: number;
}

export function hexToRgb(hex: string): RGB {
  let h = hex.replace('#', '').trim();
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const n = parseInt(h, 16);
  if (Number.isNaN(n)) return { r: 0, g: 0, b: 0 };
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

export function rgbToHex({ r, g, b }: RGB): string {
  const c = (v: number) =>
    Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

export function rgba(hex: string, alpha: number): string {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${clamp(alpha, 0, 1)})`;
}

export function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}

/** Perceived luminance, 0–1, using the sRGB coefficients. */
export function luminance(rgb: RGB): number {
  return (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255;
}

/** Relative luminance per WCAG 2.1, used for the contrast ratio. */
function relativeLuminance({ r, g, b }: RGB): number {
  const ch = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * ch(r) + 0.7152 * ch(g) + 0.0722 * ch(b);
}

/** WCAG contrast ratio, 1–21. 4.5 is the AA threshold for body text. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(hexToRgb(a));
  const lb = relativeLuminance(hexToRgb(b));
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

export function rgbToHsl({ r, g, b }: RGB): { h: number; s: number; l: number } {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
  else if (max === gn) h = ((bn - rn) / d + 2) / 6;
  else h = ((rn - gn) / d + 4) / 6;
  return { h: h * 360, s, l };
}

export function hslToRgb(h: number, s: number, l: number): RGB {
  const hn = (((h % 360) + 360) % 360) / 360;
  if (s === 0) {
    const v = l * 255;
    return { r: v, g: v, b: v };
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hue = (t: number) => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  return {
    r: hue(hn + 1 / 3) * 255,
    g: hue(hn) * 255,
    b: hue(hn - 1 / 3) * 255,
  };
}

export function adjustLightness(hex: string, delta: number): string {
  const { h, s, l } = rgbToHsl(hexToRgb(hex));
  return rgbToHex(hslToRgb(h, s, clamp(l + delta, 0, 1)));
}

export function setSaturation(hex: string, sat: number): string {
  const { h, l } = rgbToHsl(hexToRgb(hex));
  return rgbToHex(hslToRgb(h, clamp(sat, 0, 1), l));
}

/**
 * Pull a color toward a target lightness/saturation band so an accent taken
 * from the photo reads as deliberate rather than muddy.
 */
export function toAccent(hex: string, scheme: 'light' | 'dark'): string {
  const { h, s, l } = rgbToHsl(hexToRgb(hex));
  const targetS = clamp(Math.max(s, 0.42), 0, 0.78);
  const targetL = scheme === 'light' ? clamp(Math.max(l, 0.6), 0.55, 0.78) : clamp(Math.min(l, 0.46), 0.3, 0.48);
  return rgbToHex(hslToRgb(h, targetS, targetL));
}

/**
 * k-means over a pixel sample. Small k and a fixed iteration count keep this
 * well under a frame even on a 4000px upload, because the caller downsamples
 * before we ever see the data.
 */
export function extractPalette(
  pixels: Uint8ClampedArray,
  k = 5,
  iterations = 12,
): { hex: string; weight: number }[] {
  const points: RGB[] = [];
  for (let i = 0; i < pixels.length; i += 4) {
    if (pixels[i + 3] < 128) continue;
    points.push({ r: pixels[i], g: pixels[i + 1], b: pixels[i + 2] });
  }
  if (points.length === 0) return [{ hex: '#808080', weight: 1 }];

  // Deterministic seeding: spread the initial centroids across the sample so
  // repeated runs on the same photo produce the same palette.
  const centroids: RGB[] = [];
  for (let i = 0; i < k; i++) {
    centroids.push(points[Math.floor((i + 0.5) * (points.length / k))]);
  }

  const assign = new Array<number>(points.length).fill(0);
  for (let it = 0; it < iterations; it++) {
    let moved = false;
    for (let i = 0; i < points.length; i++) {
      let best = 0;
      let bestDist = Infinity;
      for (let c = 0; c < centroids.length; c++) {
        const dr = points[i].r - centroids[c].r;
        const dg = points[i].g - centroids[c].g;
        const db = points[i].b - centroids[c].b;
        const d = dr * dr + dg * dg + db * db;
        if (d < bestDist) {
          bestDist = d;
          best = c;
        }
      }
      if (assign[i] !== best) {
        assign[i] = best;
        moved = true;
      }
    }
    const sums = centroids.map(() => ({ r: 0, g: 0, b: 0, n: 0 }));
    for (let i = 0; i < points.length; i++) {
      const s = sums[assign[i]];
      s.r += points[i].r;
      s.g += points[i].g;
      s.b += points[i].b;
      s.n++;
    }
    for (let c = 0; c < centroids.length; c++) {
      if (sums[c].n > 0) {
        centroids[c] = {
          r: sums[c].r / sums[c].n,
          g: sums[c].g / sums[c].n,
          b: sums[c].b / sums[c].n,
        };
      }
    }
    if (!moved && it > 0) break;
  }

  const counts = centroids.map(() => 0);
  for (const a of assign) counts[a]++;

  return centroids
    .map((c, i) => ({ hex: rgbToHex(c), weight: counts[i] / points.length }))
    .filter((c) => c.weight > 0.01)
    .sort((a, b) => b.weight - a.weight);
}

/**
 * Choose the text color with the strongest contrast against a background,
 * preferring the requested scheme but flipping when the preferred choice
 * would be unreadable.
 */
export function pickReadableText(
  backgroundHex: string,
  scheme: 'light' | 'dark',
): { color: string; ratio: number; flipped: boolean } {
  const light = '#ffffff';
  const dark = '#0d0d0f';
  const preferred = scheme === 'light' ? light : dark;
  const other = scheme === 'light' ? dark : light;
  const preferredRatio = contrastRatio(backgroundHex, preferred);
  const otherRatio = contrastRatio(backgroundHex, other);
  if (preferredRatio >= 4.5 || preferredRatio >= otherRatio) {
    return { color: preferred, ratio: preferredRatio, flipped: false };
  }
  return { color: other, ratio: otherRatio, flipped: true };
}

/** Blend two colors, `t` = 0 returns `a`. */
export function mix(a: string, b: string, t: number): string {
  const ca = hexToRgb(a);
  const cb = hexToRgb(b);
  return rgbToHex({
    r: ca.r + (cb.r - ca.r) * t,
    g: ca.g + (cb.g - ca.g) * t,
    b: ca.b + (cb.b - ca.b) * t,
  });
}

/**
 * Effective background color once a scrim of `alpha` in `overlayHex` sits over
 * a region whose mean color is `baseHex`. Lets us verify contrast against what
 * the viewer actually sees rather than the raw photo.
 */
export function compositeOver(
  baseHex: string,
  overlayHex: string,
  alpha: number,
): string {
  return mix(baseHex, overlayHex, clamp(alpha, 0, 1));
}
